import {
	createLog,
	INJECTED_FEE_CODES,
	type InspectionServiceConfig,
	type InspectionServiceConfigDtoInput,
	type InspectionServiceConfigDtoOutput,
	inspectionServiceConfigDtoSchema,
	LogSeverity,
	OPERATIONS_SECTIONS,
	PricingModel,
	Services,
	type ServicesProvider,
	SETTINGS_GROUPS,
} from '@ekhodealer/ekho-common';
import { Form } from '@ekhodealer/ekho-common/rev';

import { useCurrentServicesProvider } from '@common/hooks/useCurrentServicesProvider';

import { useUpdateInspectionService } from '../../../api/configuration/services/useUpdateInspectionService';
import { useServiceProgramInfo } from '../../../hooks/useServiceProgramInfo';
import { FormSection } from '../../config-mgmt/FormSection';

/**
 * Helper function to get initial form data from ServicesProvider
 */
function getDefaultValues(
	servicesProvider: ServicesProvider | undefined | null,
): InspectionServiceConfigDtoInput {
	const inspectionConfig = servicesProvider?.config?.[Services.INSPECTION] as
		| InspectionServiceConfig
		| undefined;

	if (!inspectionConfig?.pricing?.length) {
		return {
			supported: false,
			pricing: [
				{
					model: PricingModel.FLAT_FEE,
					modelDetails: {
						flatFee: 0,
					},
				},
			],
		};
	}

	const firstPricing = inspectionConfig.pricing[0];
	return {
		supported: inspectionConfig.supported ?? false,
		pricing: [firstPricing],
	};
}

export function InspectionServiceForm() {
	const { data: servicesProvider } = useCurrentServicesProvider();
	const programInfo = useServiceProgramInfo(OPERATIONS_SECTIONS.INSPECTION);
	const updateInspectionServiceMutation = useUpdateInspectionService();

	// Early return if service is not part of the program
	if (!programInfo.shouldRender) {
		return null;
	}

	// Handle form submit
	const handleSubmit = async (
		data: InspectionServiceConfigDtoOutput,
	): Promise<{ isSuccess: boolean }> => {
		if (!servicesProvider) return { isSuccess: false };

		// Transform DTO to API payload format
		const dataToSubmit = {
			supported: data.supported,
			pricing: [
				{
					...data.pricing[0],
					code: INJECTED_FEE_CODES.INSPECTION_FEE,
					feeDescription: 'Inspection fee',
				},
			],
		};

		try {
			await updateInspectionServiceMutation.mutateAsync(dataToSubmit);
			return { isSuccess: true };
		} catch (error) {
			createLog(
				'config-mgmt',
				`Failed to update inspection service configuration: ${
					error instanceof Error ? error.message : String(error)
				}`,
				LogSeverity.ERROR,
			);
			return { isSuccess: false };
		}
	};

	return (
		<FormSection
			sectionPath={{
				group: SETTINGS_GROUPS.OPERATIONS,
				section: OPERATIONS_SECTIONS.INSPECTION,
			}}
			form={{
				schema: inspectionServiceConfigDtoSchema,
				defaultValues: getDefaultValues(servicesProvider),
				onSubmit: handleSubmit,
			}}
			title='Inspection'
			description='Set the default pre-delivery inspection (PDI) fee charged to buyers at checkout. Covers the cost of inspecting the vehicle before delivery or pickup.'
			formTitle='PDI pricing'
			toggleSettings={{
				fieldName: 'supported',
				visibility: 'write',
			}}
			hideDivider>
			<Form.Field.Pricing
				schemaField='pricing'
				supportedModels={[PricingModel.FLAT_FEE, PricingModel.VEHICLE_TYPE_CC]}
				modelLabelText='PDI Fee'
				modelDescriptionText='Fee charged to the buyer at checkout for pre-delivery inspection. This can be customized per vehicle.'
			/>
		</FormSection>
	);
}
